import type { MercatelyWidgetConfig } from './types';

declare const __WIDGET_VERSION__: string;
declare const __WIDGET_CDN_URL__: string;

declare global {
  interface Window {
    __MercatelyWidgetBooted?: boolean;
    MercatelyWhatsAppWidgetConfig?: MercatelyWidgetConfig;
  }
}

const script = document.currentScript as HTMLScriptElement | null;

function getCdnBase(): string | null {
  const configuredCdn =
    typeof __WIDGET_CDN_URL__ !== 'undefined' && __WIDGET_CDN_URL__ ? __WIDGET_CDN_URL__ : null;

  if (!script?.src) return configuredCdn;

  try {
    const url = new URL(script.src);

    if (url.hostname.includes('shopify')) {
      return configuredCdn;
    }

    return configuredCdn || url.origin;
  } catch {
    return configuredCdn;
  }
}

function getWidgetId(): string | null {
  const dataWidgetId = script?.dataset.widgetId;
  if (dataWidgetId) return dataWidgetId;

  if (!script?.src) return null;

  const url = new URL(script.src);
  return url.searchParams.get('wid');
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const element = document.createElement('script');
    element.async = true;
    element.src = src;
    element.onload = () => resolve();
    element.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(element);
  });
}

async function boot() {
  if (window.__MercatelyWidgetBooted) return;
  window.__MercatelyWidgetBooted = true;

  const cdnBase = getCdnBase();
  const widgetId = getWidgetId();

  if (!cdnBase || !widgetId) return;

  const configUrl = `${cdnBase}/widget/configs/${encodeURIComponent(widgetId)}.json`;
  const response = await fetch(configUrl, { credentials: 'omit', cache: 'no-store' });
  if (!response.ok) return;

  const config = (await response.json()) as MercatelyWidgetConfig;
  if (!config.active) return;

  window.MercatelyWhatsAppWidgetConfig = config;
  await loadScript(`${cdnBase}/widget/${__WIDGET_VERSION__}/widget.js`);
}

boot().catch(() => {
  // The widget should never break the host site.
});
